import type { ListProps } from '@mui/material';
import {
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import type { FormikProps } from 'formik';
import type { ReactNode } from 'react';

interface Props<ID, T, I> {
  disabled?: boolean;
  formik: FormikProps<T>;
  id: ID;
  items: I[];
  ListProps?: ListProps;
}

export function FormikCheckList<
  ID extends string,
  T extends { [key in ID]?: string[] },
  I extends { id: string; display: ReactNode },
>(props: Props<ID, T, I>): JSX.Element {
  const { formik, items } = props;
  const value = formik.values[props.id] as string[];
  const toggle = (item: I) => () =>
    formik.setFieldValue(
      props.id,
      value.includes(item.id)
        ? value.filter((id) => id !== item.id)
        : [...value, item.id],
    );
  return (
    <List sx={{ flexGrow: 1 }} {...props.ListProps}>
      {items.map((item) => {
        const labelId = `checkbox-list-label-${item.id}`;

        return (
          <ListItem
            disabled={formik.isSubmitting || props.disabled}
            disablePadding
            key={item.id}
          >
            {/* biome-ignore lint/a11y/useValidAriaRole: ignore */}
            <ListItemButton dense onClick={toggle(item)} role={undefined}>
              <ListItemIcon>
                <Checkbox
                  checked={value.includes(item.id)}
                  disabled={formik.isSubmitting || props.disabled}
                  disableRipple
                  edge="start"
                  inputProps={{ 'aria-labelledby': labelId }}
                  tabIndex={-1}
                />
              </ListItemIcon>
              <ListItemText id={labelId} primary={item.display} />
            </ListItemButton>
          </ListItem>
        );
      })}
    </List>
  );
}
